import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from "@angular/core";

/**
 * Pill / chip rendering a service-type label (e.g. "2 Day Processing",
 * "10 Business Days", "Standard"). Maps the label to a CSS slug class so
 * callers can theme specific service types via global SCSS overrides.
 *
 * Slugs:
 * - "N Day ..." / "N Day Processing" → `service-chip-Nday`
 * - "N Business Days" → `service-chip-Nbusinessday`
 * - otherwise → whole-label slug (lowercased, whitespace stripped)
 *
 * Renders an em-dash placeholder when `name` is null / empty.
 */
@Component({
  selector: "shared-service-type-chip",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (name()) {
      <span [class]="chipClass()">{{ name() }}</span>
    } @else {
      —
    }
  `,
  styles: [
    `
      .service-chip {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
        background-color: #eceff1;
        color: #455a64;
      }

      .service-chip--lg {
        padding: 4px 12px;
        border-radius: 16px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        vertical-align: middle;
      }

      .service-chip-2day,
      .service-chip-2businessday {
        background-color: #ffd700;
        color: #5c4700;
      }

      .service-chip-10day {
        background-color: #e8f5e9;
        color: #2e7d32;
      }

      .service-chip-10businessday {
        background-color: #e3f2fd;
        color: #1565c0;
      }
    `,
  ],
})
export class ServiceTypeChipComponent {
  readonly name = input<string | null | undefined>(null);
  readonly size = input<"sm" | "lg">("sm");

  readonly chipClass = computed(() => {
    const raw = this.name();
    if (!raw) return "";
    const trimmed = raw.trim().toLowerCase();
    const leadingDays = /^(\d+)\s+(business\s+)?days?\b/.exec(trimmed);
    const slug = leadingDays
      ? `${leadingDays[1]}${leadingDays[2] ? "businessday" : "day"}`
      : trimmed.replace(/\s+/g, "");
    const sizeClass = this.size() === "lg" ? " service-chip--lg" : "";
    return `service-chip service-chip-${slug}${sizeClass}`;
  });
}
